/** Reference data as the admin API serves it. */

export interface Country {
  code: string;
  name: string;
}

export interface Airport {
  code: string;
  name: string;
  city: string;
  country: string;
  timezone: string;
  latitude: number | null;
  longitude: number | null;
}

export interface City {
  code: string;
  airports: string[];
}

export interface AirlineClasses {
  first: string;
  business: string;
  premium: string;
  economy: string;
}

export interface Airline {
  code: string;
  name: string;
  country: string;
  classes: AirlineClasses;
}

export interface Aircraft {
  code: string;
  name: string;
  maxKm: number;
  wide: boolean;
  hasFirst: boolean;
}

export interface FlightKey {
  carrier: string;
  number: string;
  origin: string;
}

export interface Flight extends FlightKey {
  destination: string;
  originTerminal: string;
  destinationTerminal: string;
  /** Local minutes past midnight at each end. */
  departMinute: number;
  arriveMinute: number;
  arriveDayOffset: number;
  aircraft: string;
  /** ISO weekday bits: Monday is bit 0, Sunday bit 6. */
  daysMask: number;
}

export interface ItineraryLeg extends FlightKey {
  destination: string;
  departMinute: number;
  arriveMinute: number;
}

export interface Itinerary {
  id: number;
  first: ItineraryLeg;
  second: ItineraryLeg;
}

export interface FareRuleClass {
  class: string;
  basis?: string;
  /** US dollars; absent leaves the engine's own ladder fare. */
  fare?: number;
}

export interface FareRule {
  id: number;
  carrier: string;
  origin: string;
  destination: string;
  bothWays: boolean;
  classes: FareRuleClass[];
  note: string;
}

export type Cabin = '' | 'economy' | 'premium' | 'business' | 'first';

/** One tax, fee or surcharge on the host's tax schedule. Blank codes match anything. */
export interface TaxRule {
  id: number;
  code: string;
  name: string;
  perSector: boolean;
  origin: string;
  destination: string;
  carrier: string;
  cabin: Cabin;
  amount: number;
  stepKm: number;
  stepAmount: number;
  minimum: number;
  currency: string;
  position: number;
}

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** 615 → "10:15". */
export function formatMinute(minute: number): string {
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** "10:15" → 615, or NaN for anything that is not a clock time. */
export function parseMinute(value: string): number {
  const match = /^(\d{1,2}):?(\d{2})$/.exec(value.trim());
  if (!match) return NaN;
  const h = Number(match[1]);
  const m = Number(match[2]);
  return h < 24 && m < 60 ? h * 60 + m : NaN;
}

/** 127 → "Daily", 31 → "Mon Tue Wed Thu Fri". */
export function formatDays(mask: number): string {
  if (mask === 127) return 'Daily';
  return WEEKDAYS.filter((_, i) => mask & (1 << i)).join(' ');
}

export function flightPath(key: FlightKey): string {
  return [key.carrier, key.number, key.origin].map(encodeURIComponent).join('/');
}
